import React, { useState } from 'react';
import { Image as ImageIcon, AlertCircle } from 'lucide-react';
import { useTranslation } from '../../contexts/TranslationContext';
import { Resource, UIComponent } from '../../types';

interface ImageComponentProps {
  resource: Resource;
  component: UIComponent;
  onAction?: (action: string, data?: any) => void;
}

export const ImageComponent: React.FC<ImageComponentProps> = ({ resource, component, onAction }) => {
  const { t } = useTranslation();
  const [imageError, setImageError] = useState(false);
  
  // Get image URL from resource
  const imageUrl = resource.url || resource.src || resource.image_url || '';
  const altText = resource.alt || resource.alt_text || resource.title || t('image.image');
  const width = resource.width || resource.max_width || 'auto';
  const height = resource.height || resource.max_height || 'auto';
  const alignment = resource.alignment || resource.align || 'center'; // 'left', 'center', 'right'
  const fit = resource.fit || 'contain'; // 'contain', 'cover', 'fill'
  const caption = resource.caption || resource.description || '';

  const handleImageLoad = () => {
    setImageError(false);
    onAction?.('image_loaded', { url: imageUrl });
  };

  const handleImageError = () => {
    setImageError(true);
    onAction?.('image_error', { url: imageUrl });
  };

  const getAlignmentClass = () => {
    switch (alignment) {
      case 'left':
        return 'justify-start';
      case 'right':
        return 'justify-end';
      case 'center':
      default:
        return 'justify-center';
    }
  };

  const getImageStyle = () => {
    const style: React.CSSProperties = {
      objectFit: fit,
    };

    if (width !== 'auto') {
      style.width = typeof width === 'number' ? `${width}px` : width;
      style.maxWidth = typeof width === 'number' ? `${width}px` : width;
    }

    if (height !== 'auto') {
      style.height = typeof height === 'number' ? `${height}px` : height;
      style.maxHeight = typeof height === 'number' ? `${height}px` : height;
    }

    return style;
  };

  if (!imageUrl) {
    return (
      <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-8 text-center">
        <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600 font-medium">{t('image.noImageUrl')}</p>
        <p className="text-sm text-gray-500 mt-2">{t('image.provideImageUrl')}</p>
      </div>
    );
  }

  if (imageError) {
    return (
      <div className="bg-red-50 rounded-lg border-2 border-red-200 p-6 text-center">
        <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
        <p className="text-red-700 font-medium">{t('image.loadError')}</p>
        <p className="text-sm text-red-600 mt-2">{imageUrl}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      {caption && (
        <div className="mb-3 text-center">
          <p className="text-sm text-gray-600 font-medium">{caption}</p>
        </div>
      )}
      
      <div className={`flex ${getAlignmentClass()}`}>
        <img
          src={imageUrl}
          alt={altText}
          onLoad={handleImageLoad}
          onError={handleImageError}
          style={getImageStyle()}
          className="rounded-lg shadow-sm"
        />
      </div>
    </div>
  );
};

